import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { useI18n } from "@/i18n"

type LegalLayoutProps = {
  titleKey: string
  /** ISO date of the current draft revision. */
  lastUpdated: string
  children: ReactNode
}

/**
 * Shared chrome for legal pages (Privacy, Terms).
 *
 * WHY a prominent draft banner: the body text is a placeholder pending legal
 * review (CL-01/CL-02). It must never be mistaken for the published version.
 * Body content is canonical pt-br; other languages are CL-04 — when the active
 * language isn't pt-br we surface the translation-pending notice instead of
 * silently showing Portuguese.
 */
export const LegalLayout = ({
  titleKey,
  lastUpdated,
  children,
}: LegalLayoutProps) => {
  const { t, language } = useI18n()

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <Link
        to="/"
        className="text-sm text-gray-400 underline hover:text-gray-200"
      >
        ← {t("legal.backHome")}
      </Link>

      <h1 className="mt-8 text-3xl font-bold text-gray-100">{t(titleKey)}</h1>

      <p className="mt-2 text-sm text-gray-500">
        {t("legal.lastUpdated")}: {lastUpdated}
      </p>

      <div className="mt-6 rounded-md border border-amber-700/60 bg-amber-950/40 px-4 py-3">
        <p className="text-sm font-semibold text-amber-300">
          {t("legal.draftBadge")}
        </p>
        <p className="mt-1 text-sm text-amber-200/90">
          {t("legal.draftNotice")}
        </p>
      </div>

      {language !== "pt-br" && (
        <p className="mt-4 text-sm text-gray-500 italic">
          {t("legal.translationPending")}
        </p>
      )}

      <div className="mt-10 space-y-8">{children}</div>
    </main>
  )
}

type LegalSectionProps = {
  /** i18n key under legal.terms.sections.* or legal.privacy.sections.* */
  headingKey: string
  children: ReactNode
}

/** A titled block of legal prose. Keeps the two pages DRY. */
export const LegalSection = ({ headingKey, children }: LegalSectionProps) => {
  const { t } = useI18n()

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-100">{t(headingKey)}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-gray-300">
        {children}
      </div>
    </section>
  )
}
